import type { SessionUser } from "./sessionUser";
import { displayNameForUser } from "./sessionUser";

export type RegistryAuthType = "header" | "oauth" | "none";

export type RegistryItem = {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  icon: string;
  fileUrl: string;
  downloadCount: number;
  starCount: number;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  isStarred: boolean;
  endpointCount: number;
  /** Present when this MCP requires authentication */
  authType?: RegistryAuthType;
  /** Human-readable label for the credential, e.g. "Notion Integration Token" */
  authLabel?: string;
  /** Curated/verified by the platform team */
  verified?: boolean;
  /** Total tool calls in the last 7 days */
  weeklyCallCount?: number;
};

export type RegistryComment = {
  id: string;
  content: string;
  upvotes: number;
  createdAt: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
  };
};

export type RegistryDetails = Omit<RegistryItem, "commentCount"> & {
  comments: RegistryComment[];
  /** When set, detail page shows only this MCP endpoint (no ZIP download / setup guide). */
  linkOnlyUrl?: string;
  /** Absolute URL for opening the hosted MCP from the browser (dev/prod). */
  linkOnlyUrlResolved?: string;
  features?: string[];
  /** Live count of tools/endpoints exposed by this MCP. */
  endpointCount?: number;
  /** Full auth details — only present on detail endpoint */
  authInstructions?: string;
  /** HTTP header name to set, e.g. "Authorization" */
  credentialHeader?: string;
  /** Env var / key name for the credential, e.g. "NOTION_TOKEN" */
  credentialKey?: string;
};

async function parseError(response: Response, fallback: string) {
  try {
    const json = (await response.json()) as {
      error?: string;
      detail?: string | Array<{ msg?: string }>;
    };
    if (json.error) return json.error;
    const d = json.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d.length && typeof d[0]?.msg === "string") return d.map((x) => x.msg).join(", ");
  } catch {
    // ignore
  }
  return fallback;
}

const registryFetchInit: RequestInit = { credentials: "include", cache: "no-store" };

function registryApiUrl(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

type RegistryFetchOptions = {
  signal?: AbortSignal;
};

export async function fetchRegistryList(options: RegistryFetchOptions = {}) {
  const response = await fetch(registryApiUrl("/api/registry"), {
    ...registryFetchInit,
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to fetch registry"));
  }
  return (await response.json()) as RegistryItem[];
}

export async function fetchRegistryDetails(slug: string, options: RegistryFetchOptions = {}) {
  const response = await fetch(registryApiUrl(`/api/registry/${encodeURIComponent(slug)}`), {
    ...registryFetchInit,
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to fetch MCP details"));
  }
  return (await response.json()) as RegistryDetails;
}

export async function toggleRegistryStar(slug: string) {
  const response = await fetch(registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/star`), {
    method: "POST",
    ...registryFetchInit,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to toggle star"));
  }
  return (await response.json()) as { isStarred: boolean; starCount: number };
}

function sessionHeaders(user: SessionUser): HeadersInit {
  const email = user.email?.trim().toLowerCase();
  if (!email) return {};
  return {
    "X-Registry-User-Email": email,
    "X-Registry-User-Name": displayNameForUser(user),
  };
}

export async function postRegistryComment(slug: string, content: string, user: SessionUser) {
  const response = await fetch(registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/comments`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...sessionHeaders(user),
    },
    body: JSON.stringify({ content }),
    ...registryFetchInit,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to post comment"));
  }
  return (await response.json()) as RegistryComment;
}

export async function editRegistryComment(slug: string, commentId: string, content: string, user: SessionUser) {
  const response = await fetch(
    registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}`),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...sessionHeaders(user),
      },
      body: JSON.stringify({ content }),
      ...registryFetchInit,
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to edit comment"));
  }
  return (await response.json()) as RegistryComment;
}

export async function deleteRegistryComment(slug: string, commentId: string, user: SessionUser) {
  const response = await fetch(
    registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/comments/${encodeURIComponent(commentId)}`),
    {
      method: "DELETE",
      headers: sessionHeaders(user),
      ...registryFetchInit,
    },
  );
  if (response.status !== 204 && !response.ok) {
    throw new Error(await parseError(response, "Failed to delete comment"));
  }
}

export async function registerRegistryDownload(slug: string) {
  const response = await fetch(registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/download`), {
    method: "POST",
    ...registryFetchInit,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to start download"));
  }
  return (await response.json()) as { downloadCount: number; downloadUrl: string };
}

// ── Registry Connections ───────────────────────────────────────────────────

export type RegistryConnection = {
  connectionId: string;
  slug: string;
  name: string;
  icon: string;
  mcpUrl: string;
  connectedAt: string;
  lastUsedAt: string | null;
};

export async function connectRegistryMcp(slug: string, token: string, user: SessionUser) {
  const response = await fetch(registryApiUrl(`/api/registry/${encodeURIComponent(slug)}/connect`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...sessionHeaders(user),
    },
    body: JSON.stringify({ token }),
    ...registryFetchInit,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to connect MCP"));
  }
  return (await response.json()) as {
    connectionId: string;
    slug: string;
    mcpUrl: string;
    connectedAt: string;
  };
}

export async function listRegistryConnections(user: SessionUser) {
  const response = await fetch(registryApiUrl("/api/registry/connections"), {
    headers: sessionHeaders(user),
    ...registryFetchInit,
  });
  if (!response.ok) {
    throw new Error(await parseError(response, "Failed to list connections"));
  }
  return (await response.json()) as RegistryConnection[];
}

export async function revokeRegistryConnection(connectionId: string, user: SessionUser) {
  const response = await fetch(
    registryApiUrl(`/api/registry/connections/${encodeURIComponent(connectionId)}`),
    {
      method: "DELETE",
      headers: sessionHeaders(user),
      ...registryFetchInit,
    },
  );
  if (response.status !== 204 && !response.ok) {
    throw new Error(await parseError(response, "Failed to revoke connection"));
  }
}
